/**
 * One place that turns a PMS gate verdict (PRX-59's `pmsRxGate`) into what the
 * UI shows for it — a status chip label/colour. PRX-71: the hardcopy printed
 * "Unknown" for a Fillware prescription because nothing ever translated the
 * gate into a status; this is that translation, shared by `HardcopyViewer`
 * (a chip standing in for `RxStatusBadge` when `status` isn't the Kroll
 * number) and `PmsGateBanner`'s Alert, so the two surfaces can never disagree
 * about what a verdict means.
 *
 * One colour axis, not two: `color` is `PmsGateBanner`'s own pre-existing
 * severity mapping (cancelled is the only red; a frozen fill reads amber —
 * a return-to-stock nudge, not a hard stop), reused as-is for the chip
 * instead of inventing a second, conflicting scheme.
 */
import type { PmsGateResult, PmsGateVerdict } from '../../types/pmsGate';

export interface PmsGateDisplay {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
}

const detailFor = (gate: PmsGateResult): string => gate.message || gate.reason || '';

const labelFor = (gate: PmsGateResult): string => {
  const detail = detailFor(gate);
  switch (gate.verdict) {
    case 'flows':
      return 'Active';
    case 'warned':
      return detail ? `Active — ${detail}` : 'Active';
    case 'frozen':
      return detail ? `On hold — ${detail}` : 'On hold';
    case 'cancelled':
      return detail ? `Cancelled — ${detail}` : 'Cancelled';
    default:
      return 'Status unavailable';
  }
};

const colorFor = (verdict: PmsGateVerdict): PmsGateDisplay['color'] => {
  switch (verdict) {
    case 'flows':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'warned':
    case 'frozen':
    default:
      return 'warning';
  }
};

/**
 * `null`/`undefined` means the gate was never computed (a Fillware row with
 * no gate columns, or a fetch that failed) — never render "Unknown", and
 * never guess a verdict. That state renders "Status unavailable" instead.
 */
export function describePmsGate(gate: PmsGateResult | null | undefined): PmsGateDisplay {
  if (!gate) return { label: 'Status unavailable', color: 'default' };
  return { label: labelFor(gate), color: colorFor(gate.verdict) };
}

/**
 * PRX-165 B1: one short word per gate code, for a table cell. Mirrors the
 * codes `fillwareRxGate` emits (`backend-v2/src/common/fillware/rx-gate.ts`).
 */
const SHORT_WORD_BY_CODE: Record<string, string> = {
  'fillware.active': 'Billed',
  'fillware.pending-billing': 'Not billed',
  'fillware.pending-billing-no-plan': 'Cash, not claimed',
  'fillware.claim-rejected': 'Claim rejected',
  'fillware.claim-reversed': 'Claim reversed',
  'fillware.on-hold': 'On hold',
  'fillware.transfer-in-held': 'Transfer in pending',
  'fillware.inactive': 'Discontinued',
  'fillware.deleted': 'Cancelled',
  'fillware.transferred-out': 'Transferred out',
  'fillware.not-found': 'Not found in Fillware',
};

/** A code this map has never seen reads the verdict itself, never a guessed status. */
const VERDICT_WORD: Record<PmsGateVerdict, string> = {
  flows: 'Active',
  warned: 'Active',
  frozen: 'On hold',
  cancelled: 'Cancelled',
};

export interface PmsGateShortDisplay extends PmsGateDisplay {
  /** The gate's sentence plus the PMS's reason; null when there is no gate. */
  tooltip: string | null;
}

/**
 * The table-cell form of a gate: a short word, the same colour as
 * `describePmsGate`, and the full sentence for a tooltip. `describePmsGate`
 * stays as it is for the hardcopy and `PmsGateBanner`, which have room.
 */
export function describePmsGateShort(gate: PmsGateResult | null | undefined): PmsGateShortDisplay {
  if (!gate) return { label: 'Status unavailable', color: 'default', tooltip: null };
  const label = SHORT_WORD_BY_CODE[gate.code] ?? VERDICT_WORD[gate.verdict] ?? 'Status unavailable';
  const tooltip = [gate.message, gate.reason ? `Reason: ${gate.reason}` : ''].filter(Boolean).join(' ') || null;
  return { label, color: colorFor(gate.verdict), tooltip };
}
